import { Controller } from "@hotwired/stimulus"

// One <dialog>, two open modes chosen at open time by viewport width and held
// until close:
//   desktop → showPopover()  nonmodal, top layer, anchored under the trigger
//   mobile  → showModal()    modal sheet (.dialog[data-mobile])
//
// Both modes render in the top layer, so the panel escapes ancestor overflow
// and stacking contexts without portaling to <body>.
//
// Escape + outside dismissal work in both modes: native light dismiss for the
// auto popover; native cancel plus a backdrop handler for the modal.

// The one breakpoint. popup.css hardcodes the same value; they must agree, so
// neither side is configurable.
const BREAKPOINT = 768

export default class extends Controller {
  static targets = ["dialog", "trigger"]

  // Chrome animates the modal close via native CSS transitions; Safari/Firefox
  // do not, so there we add .closing and let the dialog-close-* keyframes run
  // (same detection dialog_controller uses).
  static supportsNativeExitAnimation =
    CSS.supports("overlay", "auto") && CSS.supports("transition-behavior", "allow-discrete")

  connect() {
    this.wasOpen = false
    this.desktopQuery = window.matchMedia(`(min-width: ${BREAKPOINT}px)`)
    this.desktopQuery.addEventListener("change", this.closeOnBreakpointCross)
  }

  disconnect() {
    this.desktopQuery.removeEventListener("change", this.closeOnBreakpointCross)
  }

  // The mode is held until close, but the CSS follows the viewport live, so a
  // panel left open across the breakpoint would pair one mode's semantics with
  // the other's geometry. Drop it instead, and without animation — the exit
  // belongs to the mode that is already gone.
  closeOnBreakpointCross = () => {
    if (this.isOpen) this.dismiss()
  }

  // A trigger pointerdown fires before the popover's light dismiss closes an
  // already-open auto popover, so record the state here and skip reopening it
  // in the click handler that follows.
  recordState() {
    this.wasOpen = this.isOpen
  }

  // Keyboard activation never fires pointerdown, so wasOpen has to be cleared
  // by the click it suppresses — left standing, it swallows the next Enter.
  toggle() {
    if (this.wasOpen) {
      this.wasOpen = false
    } else if (this.isOpen) {
      this.close()
    } else {
      this.show()
    }
  }

  show() {
    if (this.desktop) {
      this.dialogTarget.setAttribute("popover", "auto")
      this.dialogTarget.showPopover()
    } else {
      this.dialogTarget.removeAttribute("popover")
      this.dialogTarget.showModal()
      this.manageFocus()
    }

    this.syncExpanded()
  }

  // showModal() moves focus to the first focusable child, and mobile Safari
  // paints its focus ring there — on a sheet that lands on the first row of the
  // panel, which reads as a stray box around an item nobody picked. A body that
  // wants focus asks for it with [autofocus]; otherwise nothing is focused.
  // Only the modal branch runs this, and that branch is the mobile one.
  manageFocus() {
    const autofocused = this.dialogTarget.querySelector("[autofocus]")

    if (autofocused) {
      autofocused.focus()
    } else {
      document.activeElement?.blur()
    }
  }

  // Native light dismiss and Escape never reach close(), so aria-expanded can't
  // be written there — it would stay stale at "true". The dialog's own toggle
  // (popover) and close (modal) events fire for those dismissals too, and both
  // are bound in the template, so read the state rather than track it.
  syncExpanded() {
    this.triggerTarget.setAttribute("aria-expanded", this.isOpen)
  }

  close() {
    if (!this.isOpen) return

    if (this.constructor.supportsNativeExitAnimation) {
      this.dismiss()
    } else {
      this.animateClose()
    }
  }

  // Stimulus action: turbo:submit-end->popup#submitEnd
  // Success closes the panel; a validation failure re-renders inside it and has
  // to stay on screen, so popup#close must not be bound to submit-end directly.
  submitEnd(event) {
    if (event.detail.success) this.close()
  }

  // Turbo caches the DOM as it stands, and a restored snapshot with dialog[open]
  // in it leaves the page scroll-locked behind a panel nobody can reach. The
  // snapshot is taken synchronously, so the animated close is not an option.
  cacheClose() {
    if (this.isOpen) {
      this.dialogTarget.classList.remove("closing")
      this.dismiss()
    }
  }

  // Safari keeps a modal in the top layer when a morph strips [open], and close() then no-ops on an inert page.
  keepOpenState(event) {
    if (event.target === this.dialogTarget && this.isOpen && ["open", "class"].includes(event.detail.attributeName)) {
      event.preventDefault()
    }
  }

  // Native dismissal — Chrome animates the close via the base CSS transition.
  // Popover close is anchor-preserving; modal close returns focus to the trigger.
  dismiss() {
    if (this.dialogTarget.matches(":popover-open")) {
      this.dialogTarget.hidePopover()
    } else {
      this.dialogTarget.close()
    }
  }

  // Safari/Firefox close animation: add .closing so the dialog-close-* keyframe
  // plays (mobile slide, desktop scale+fade), then dismiss once it finishes.
  animateClose() {
    const dialog = this.dialogTarget
    if (dialog.classList.contains("closing")) return

    dialog.classList.add("closing")

    const cleanup = () => {
      this.dismiss()
      dialog.classList.remove("closing")
    }

    // Wait on the animation the class just started, rather than on a duration
    // read back out of CSS — the popup exits in 120ms, the modal sheet in 500ms,
    // and reduced motion in none at all.
    //
    // Deliberately not subtree: a lazy frame's spinner animates forever, and its
    // finished promise would never settle.
    const animations = dialog.getAnimations()

    if (animations.length === 0) {
      cleanup()
    } else {
      // Settle either way — a cancelled animation rejects.
      Promise.all(animations.map((animation) => animation.finished)).then(cleanup, cleanup)
    }
  }

  // showModal() keeps the dialog open on a backdrop tap; close it explicitly.
  // Only fires for the modal (dialog.open); the nonmodal popover light-dismisses.
  backdropClose(event) {
    if (event.target === this.dialogTarget && this.dialogTarget.open) this.close()
  }

  get isOpen() {
    return this.dialogTarget.matches(":popover-open") || this.dialogTarget.open
  }

  get desktop() {
    return this.desktopQuery.matches
  }
}
